import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { Dashboard } from "@dto/Dashboard";
import { DashboardRightType, FullRightAccess, hasGlobalRightAccess } from "@dto/Users";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_DashboardMenuItem, TW_DashboardMenuItemIcon, TW_ErrorMessage, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { Copy } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { z } from "zod";

interface DashboardMenuDuplicateItemProps {
  dashboard: Dashboard;
  onDashboardUpdate: (dashboard: Dashboard) => void;
  closeMenu: () => void;
}

function DashboardMenuDuplicateItem({ dashboard, onDashboardUpdate, closeMenu }: DashboardMenuDuplicateItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle("transparent"), getWsButtonStyle(wsHoverColor));
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const hasDashboardFullRight = dashboard.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, DashboardRightType, FullRightAccess, dashboard._key, true);

  const dashboardUpdated = (newDashboard: Dashboard) => {
    setIsDialogOpen(false);
    closeMenu();
    onDashboardUpdate(newDashboard);
  };

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasDashboardFullRight) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }

  const disabled = dashboard === null || !hasDashboardFullRight;
  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_DashboardMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}>
        <Copy className={TW_DashboardMenuItemIcon} />
        {t("dashboard.button.duplicate.text")}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dashboard.dialog.duplicate.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("dashboard.dialog.duplicate.description")}</DialogDescription>
        </DialogHeader>
        {dashboard && <DashboardDuplicateForm initialDashboard={dashboard} onDashboardUpdate={dashboardUpdated} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  );
}

interface DashboardDuplicateFormProps {
  initialDashboard: Dashboard;
  onDashboardUpdate: (dashboard: Dashboard) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function DashboardDuplicateForm({ initialDashboard, onDashboardUpdate, onCancel }: DashboardDuplicateFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const dashboards = useModelStore(state => state.dashboards);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    dashboardName: z.string().min(1, {
      message: t("dashboard.dialog.error.name.missing"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dashboardName: initialDashboard.name + t("dashboard.dialog.duplicate.suffix"),
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }

    const sortedDashboards = [...(dashboards ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const originalIndex = sortedDashboards.findIndex(currentDashboard => currentDashboard._key === initialDashboard._key);
    if (originalIndex === -1) {
      console.error("Dashboard not found");
      return;
    }

    let order = 0;
    if (originalIndex === sortedDashboards.length - 1) {
      order = (sortedDashboards[originalIndex].order ?? 0) + DEFAULT_ORDER_INCREMENT;
    } else {
      order = ((sortedDashboards[originalIndex].order ?? 0) + (sortedDashboards[originalIndex + 1].order ?? 0)) / 2;
    }

    const newDashboard = {
      ...initialDashboard,
      name: values.dashboardName,
      order,
    };

    post<Dashboard>(`${BASE_URL}/model/workspaces/${workspace._key}/dashboards`, newDashboard).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          setError(null);
          onDashboardUpdate(response as Dashboard);
        }
      }
    });
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="dashboardName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("dashboard.dialog.duplicate.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("dashboard.dialog.duplicate.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  );
}

export default DashboardMenuDuplicateItem;
